import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API, fmtErr } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DOCUMENT_TYPES = [
  ["sale", "Sales Invoice", "TAX INVOICE"],
  ["estimate", "Estimate", "QUOTATION"],
  ["purchase", "Purchase Bill", "PURCHASE BILL"],
  ["po", "Purchase Order", "PURCHASE ORDER"],
  ["credit_note", "Credit Note", "CREDIT NOTE"],
  ["debit_note", "Debit Note", "DEBIT NOTE"],
];

const DEFAULT_TEMPLATE = {
  layout: "classic",
  title: "",
  accent_color: "#0A2540",
  show_business_details: true,
  show_party_details: true,
  show_payment_details: true,
  show_notes: true,
  show_eway_bill: true,
  show_vehicle_no: true,
};

const LAYOUTS = [
  ["classic", "Classic", "#0A2540"],
  ["modern", "Modern", "#008A52"],
  ["elegant", "Elegant", "#5A3D78"],
  ["compact", "Compact", "#475569"],
];

const SECTION_OPTIONS = [
  ["show_business_details", "Business details"],
  ["show_party_details", "Customer / vendor details"],
  ["show_payment_details", "Payment details"],
  ["show_notes", "Notes and terms"],
  ["show_eway_bill", "E-Way Bill No."],
  ["show_vehicle_no", "Vehicle No."],
];

function templateFor(templates, type) {
  return { ...DEFAULT_TEMPLATE, ...(templates?.[type] || {}) };
}

export default function DocumentTemplateEditor({ templates, onSaved }) {
  const [documentType, setDocumentType] = useState("sale");
  const [draft, setDraft] = useState(() => templateFor(templates, "sale"));
  const [saving, setSaving] = useState(false);
  const selectedDocument = DOCUMENT_TYPES.find(([type]) => type === documentType);

  useEffect(() => {
    setDraft(templateFor(templates, documentType));
  }, [documentType, templates]);

  const update = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const selectLayout = (layout) => {
    const preset = LAYOUTS.find(([value]) => value === layout);
    setDraft((current) => ({
      ...current,
      layout,
      accent_color: preset[2],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.put(
        `/settings/document-templates/${documentType}`,
        draft,
      );
      onSaved?.(documentType, data.template);
      toast.success(`${selectedDocument[1]} template saved`);
    } catch (error) {
      toast.error(fmtErr(error));
    } finally {
      setSaving(false);
    }
  };

  const accent = /^#[0-9a-f]{6}$/i.test(draft.accent_color)
    ? draft.accent_color
    : DEFAULT_TEMPLATE.accent_color;

  return (
    <section className="space-y-4 border-t pt-4" data-testid="document-template-editor">
      <div>
        <h2 className="font-heading text-lg font-semibold tracking-tight">Document Templates</h2>
        <p className="text-xs text-muted-foreground">
          Customize the saved PDF appearance for each document type. Changes appear in previews, downloads, and emailed PDFs.
        </p>
      </div>
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.9fr)]">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="document-template-type">Document type</Label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger id="document-template-type" data-testid="document-template-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_TYPES.map(([type, label]) => (
                  <SelectItem key={type} value={type}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="document-template-layout">Layout</Label>
            <Select value={draft.layout} onValueChange={selectLayout}>
              <SelectTrigger id="document-template-layout" data-testid="document-template-layout-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LAYOUTS.map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_100px]">
            <div className="space-y-2">
              <Label htmlFor="document-template-title">Document title</Label>
              <Input
                id="document-template-title"
                value={draft.title}
                onChange={(event) => update("title", event.target.value)}
                placeholder={selectedDocument[2]}
                maxLength={50}
                data-testid="document-template-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="document-template-color">Accent color</Label>
              <Input
                id="document-template-color"
                type="color"
                className="h-10 p-1"
                value={accent}
                onChange={(event) => update("accent_color", event.target.value)}
                data-testid="document-template-color-input"
              />
            </div>
          </div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Show on document</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {SECTION_OPTIONS.map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={draft[key]}
                    onCheckedChange={(checked) => update(key, !!checked)}
                    data-testid={`document-template-${key}-check`}
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
          <Button onClick={save} disabled={saving} data-testid="document-template-save-button">
            {saving ? "Saving…" : "Save Template"}
          </Button>
        </div>

        <div className="space-y-2" data-testid="document-template-preview">
          <p className="text-sm font-medium">Live preview</p>
          <div className="min-h-[360px] rounded-md border bg-muted/40 p-3">
            <div className="mx-auto min-h-[340px] max-w-[360px] bg-white p-5 text-[10px] text-slate-700 shadow-sm">
              <div className="flex justify-between gap-3 border-b pb-3">
                {draft.show_business_details ? (
                  <div>
                    <p className="text-sm font-bold" style={{ color: accent }}>Your Business Name</p>
                    <p>Business address · City · State</p>
                    <p>GSTIN: 27ABCDE1234F1Z5 · Phone: 98765 43210</p>
                  </div>
                ) : <span />}
                <div className="text-right">
                  <p className="text-sm font-bold" style={{ color: accent }}>
                    {draft.title.trim() || selectedDocument[2]}
                  </p>
                  <p>No: DOC-0001</p>
                  <p>Date: 08/10/2026</p>
                  {draft.show_eway_bill && <p>E-Way Bill No: 121000000000</p>}
                  {draft.show_vehicle_no && <p>Vehicle No: MH12AB1234</p>}
                </div>
              </div>
              {draft.show_party_details && (
                <div className="my-4 rounded-sm bg-slate-50 p-2">
                  <p className="font-semibold">{["purchase", "po", "debit_note"].includes(documentType) ? "Bill From" : "Bill To"}</p>
                  <p>Sample Customer / Vendor</p>
                  <p>Address, City · GSTIN</p>
                </div>
              )}
              <table className="my-4 w-full border-collapse text-left">
                <thead>
                  <tr style={{ backgroundColor: accent, color: "#fff" }}>
                    <th className="p-1">S. No.</th><th className="p-1">Item &amp; Description</th><th className="p-1">HSN/SAC</th><th className="p-1">Qty</th><th className="p-1 text-right">Rate</th><th className="p-1 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b"><td className="p-1">1</td><td className="p-1">Sample product</td><td className="p-1">9983</td><td className="p-1">2 pcs</td><td className="p-1 text-right">₹500.00</td><td className="p-1 text-right">₹1,000.00</td></tr>
                </tbody>
              </table>
              <div className="ml-auto max-w-[170px] space-y-1">
                <p className="flex justify-between"><span>Taxable Value</span><span>₹1,000.00</span></p>
                <p className="flex justify-between"><span>GST</span><span>₹180.00</span></p>
                <p className="flex justify-between p-2 font-bold text-white" style={{ backgroundColor: accent }}><span>Total</span><span>₹1,180.00</span></p>
                {draft.show_payment_details && <p className="flex justify-between"><span>Balance Due</span><span>₹1,180.00</span></p>}
              </div>
              {draft.show_payment_details && <p className="mt-4 border-t pt-2">Bank details from Settings · UPI</p>}
              {draft.show_notes && <p className="mt-3 border-t pt-2">Notes and terms appear here.</p>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
